// admin/turma-picker.js — bottom-sheet iOS-style (wheel giratória) para turmas.
//
// Uso:
//   import { openTurmaPicker } from './turma-picker.js';
//   openTurmaPicker(turmaKeys, counts, currentTurma, (novaTurma) => {...});
//
// turmaKeys    : Array<string>      ex: ['31','32','33']
// counts       : Object<string,num> ex: { '31': 5, '32': 19 }
// currentTurma : string             turma atualmente selecionada
// onSelect     : function(string)   chamado só quando confirma com turma diferente

const ITEM_H  = 44;  // altura de cada item do wheel (px)
const VISIBLE = 5;   // itens visíveis (ímpar p/ ter item central)

export function openTurmaPicker(turmaKeys, counts, currentTurma, onSelect) {
  const padY = (VISIBLE - 1) / 2 * ITEM_H;
  const currentIdx = Math.max(0, turmaKeys.indexOf(String(currentTurma)));

  const backdrop = document.createElement('div');
  backdrop.style.cssText = `
    position:fixed;inset:0;background:rgba(0,0,0,0.45);
    z-index:9999;display:flex;align-items:flex-end;justify-content:center;
    animation:freqPickFade .18s ease-out;
  `;

  const sheet = document.createElement('div');
  sheet.style.cssText = `
    width:100%;max-width:520px;background:#fff;
    border-top-left-radius:22px;border-top-right-radius:22px;
    padding:10px 0 calc(env(safe-area-inset-bottom,0px) + 12px) 0;
    box-shadow:0 -8px 32px rgba(0,0,0,0.25);
    animation:freqPickSlide .22s cubic-bezier(.2,.8,.2,1);
    font-family:'Montserrat',sans-serif;
  `;

  sheet.innerHTML = `
    <style>
      @keyframes freqPickFade  { from{opacity:0} to{opacity:1} }
      @keyframes freqPickSlide { from{transform:translateY(100%)} to{transform:translateY(0)} }
      #wheel::-webkit-scrollbar { display:none }
    </style>
    <div style="width:44px;height:5px;background:#E0E0E0;border-radius:100px;margin:6px auto 14px"></div>
    <div style="padding:0 20px 8px;display:flex;align-items:center;justify-content:space-between">
      <button id="pickCancel" style="background:none;border:none;color:#6A001F;font-size:15px;font-weight:600;cursor:pointer;font-family:inherit;padding:4px 0">Cancelar</button>
      <div style="font-size:15px;font-weight:800;color:#0A0A0A">Selecionar turma</div>
      <button id="pickOk" style="background:none;border:none;color:#6A001F;font-size:15px;font-weight:800;cursor:pointer;font-family:inherit;padding:4px 0">Confirmar</button>
    </div>
    <div style="position:relative;height:${ITEM_H * VISIBLE}px">
      <div style="
        position:absolute;left:16px;right:16px;top:${padY}px;height:${ITEM_H}px;
        background:#F5F5F5;border-radius:12px;pointer-events:none;
      "></div>
      <div id="wheel" style="
        height:100%;overflow-y:auto;scroll-snap-type:y mandatory;
        -webkit-overflow-scrolling:touch;scrollbar-width:none;
      ">
        ${turmaKeys.map(t => `
          <div class="wheel-item" data-turma="${t}" style="
            height:${ITEM_H}px;display:flex;align-items:center;justify-content:center;
            scroll-snap-align:center;font-size:17px;font-weight:700;color:#0A0A0A;
          ">Turma ${t}<span style="color:#9B9B9B;font-weight:600;margin-left:8px">(${counts[t] || 0})</span></div>
        `).join('')}
      </div>
    </div>
  `;

  backdrop.appendChild(sheet);
  document.body.appendChild(backdrop);

  const wheel = sheet.querySelector('#wheel');
  wheel.style.paddingTop    = padY + 'px';
  wheel.style.paddingBottom = padY + 'px';
  wheel.scrollTop = currentIdx * ITEM_H;

  // Destaca o item centralizado
  function updateHighlight() {
    const center = wheel.scrollTop + (ITEM_H * VISIBLE) / 2;
    wheel.querySelectorAll('.wheel-item').forEach((el, i) => {
      const mid = padY + i * ITEM_H + ITEM_H / 2;
      const dist = Math.abs(mid - center);
      const near = dist < ITEM_H / 2;
      el.style.color      = near ? '#6A001F' : '#0A0A0A';
      el.style.fontWeight = near ? '800' : '600';
      el.style.transform  = `scale(${near ? 1.05 : 1})`;
      el.style.opacity    = dist > ITEM_H * 2 ? 0.35 : (dist > ITEM_H ? 0.6 : 1);
      el.style.transition = 'color .12s, font-weight .12s, transform .12s, opacity .12s';
    });
  }
  updateHighlight();
  wheel.addEventListener('scroll', updateHighlight, { passive: true });

  // Toca num item → rola pra ele
  wheel.addEventListener('click', e => {
    const item = e.target.closest('.wheel-item');
    if (!item) return;
    const i = [...wheel.querySelectorAll('.wheel-item')].indexOf(item);
    wheel.scrollTo({ top: i * ITEM_H, behavior: 'smooth' });
  });

  function currentSelection() {
    const i = Math.round(wheel.scrollTop / ITEM_H);
    return turmaKeys[Math.max(0, Math.min(turmaKeys.length - 1, i))];
  }

  function close() {
    backdrop.style.animation = 'freqPickFade .15s ease-out reverse';
    sheet.style.animation    = 'freqPickSlide .18s cubic-bezier(.2,.8,.2,1) reverse';
    setTimeout(() => backdrop.remove(), 160);
  }

  sheet.querySelector('#pickCancel').addEventListener('click', close);
  sheet.querySelector('#pickOk').addEventListener('click', () => {
    const t = currentSelection();
    close();
    if (t && t !== String(currentTurma)) onSelect(t);
  });
  backdrop.addEventListener('click', e => { if (e.target === backdrop) close(); });
}
